/**
 * TanStack Query hooks for Command Center data fetching
 *
 * Replaces the monolithic loadData() in usePropertyData with
 * independent, cacheable, parallel-loading queries.
 */

import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { authFetch, buildApiUrl } from '../../services/api/client';
import type { RoomType, PickupData } from '../../types';
import type { Freshness } from '../../components/command/ActionDrawer';
import {
  buildLosItems,
  isLosActionType,
  losVectorHasNonMinChange,
  type LosGroupBatchResponse,
  type LosGroupedGroup,
  type LosItem,
} from '../../components/command/losPriorityUtils';
import type { ReadinessEntry, ReadinessSummary } from '../../components/DataReadinessBanner';
import { fetchLOSRestrictions, type LOSRestriction } from '../../services/api/oracle';
import { getIntelEventsConfig } from '@/lib/intel/eventsConfig';
import {
  COMMAND_CENTER_FULL_DAYS_AHEAD,
  COMMAND_CENTER_FAST_DAYS_AHEAD,
} from '@/lib/commandCenterHorizon';
import {
  computeEventCompressionScore,
  dedupeNearIdenticalEvents,
  rankEventsByImpact,
  resolveEventImpactTier,
  type BackendOpportunityEvent,
} from '@/lib/intel/eventsMateriality';
import type { EventsMap, RawDecision } from './gridData';
export { buildGridData } from './gridData';
export type { FetchedDateRange, RawDecision } from './gridData';

// ============================================================================
// Query Keys - centralized for cache invalidation
// ============================================================================

export const queryKeys = {
  properties: ['properties'] as const,
  roomTypes: (property: string) => ['roomTypes', property] as const,
  roomTypeDailyNetPickup: (property: string, days: number = 7, stayDays: number = 30) =>
    ['roomTypeDailyNetPickup', property, days, stayDays] as const,
  decisions: (
    property: string,
    daysAhead: number,
    mode: 'full' | 'lite' = 'full',
    surface: 'standard' | 'grid' = 'standard',
    daysBack: number = 0,
    windowKey: string | null = null,
  ) =>
    windowKey
      ? ([
          'decisions',
          property,
          daysAhead,
          mode,
          surface,
          'back',
          daysBack,
          'window',
          windowKey,
        ] as const)
      : daysBack > 0
        ? (['decisions', property, daysAhead, mode, surface, 'back', daysBack] as const)
        : surface === 'standard'
          ? (['decisions', property, daysAhead, mode] as const)
          : (['decisions', property, daysAhead, mode, surface] as const),
  autonomySettings: (property: string) => ['autonomySettings', property] as const,
  kpis: (property: string) => ['kpis', property] as const,
  kpiHeadline: (property: string) => ['kpiHeadline', property] as const,
  kpiBootstrap: (property: string, includeDailyBreakdown: boolean) =>
    ['kpiBootstrap', property, includeDailyBreakdown ? 'full' : 'lite'] as const,
  kpiReadiness: (property: string) => ['kpiReadiness', property] as const,
  freshness: (property: string) => ['freshness', property] as const,
  trustMetrics: (property: string, mode: 'full' | 'lite' = 'full') =>
    ['trustMetrics', property, mode] as const,
  events: (property: string, days: number) => ['events', property, days] as const,
  intelEvents: (property: string, days: number) => ['intelEvents', property, days] as const,
  losRestrictions: (property: string, daysAhead?: number) =>
    daysAhead == null
      ? (['losRestrictions', property] as const)
      : (['losRestrictions', property, daysAhead] as const),
  pendingLosRecommendations: (property: string) => ['pendingLosRecommendations', property] as const,
  losOperations: (property: string) => ['losOperations', property] as const,
  losOperationCandidates: (property: string) => ['losOperationCandidates', property] as const,
};

// ============================================================================
// Types
// ============================================================================

interface Property {
  id: string;
  name: string;
  units: number;
  readOnly?: boolean;
}

interface Counts {
  pending: { total: number; inWindow: number };
  approved: { total: number; inWindow: number };
  rejected: { total: number; inWindow: number };
}

const EMPTY_COUNTS: Counts = {
  pending: { total: 0, inWindow: 0 },
  approved: { total: 0, inWindow: 0 },
  rejected: { total: 0, inWindow: 0 },
};

function deriveCountsFromDecisions(
  decisions: Array<{ id?: string | null; status?: string | null }>,
): Counts {
  const counts: Counts = {
    pending: { total: 0, inWindow: 0 },
    approved: { total: 0, inWindow: 0 },
    rejected: { total: 0, inWindow: 0 },
  };

  for (const decision of decisions) {
    if (
      !decision.id ||
      !['pending', 'blocked', 'push_failed', 'approved', 'rejected'].includes(decision.status ?? '')
    )
      continue;
    const bucket =
      decision.status === 'approved' || decision.status === 'rejected'
        ? decision.status
        : 'pending';
    counts[bucket].total += 1;
    counts[bucket].inWindow += 1;
  }

  return counts;
}

interface KPIs {
  occupancy: number;
  occupancyLy?: number;
  adr: number;
  adrLy?: number;
  revpar: number;
  revparLy?: number;
  revenue: number;
  pickup7dRevenue: number;
  pickup7dNights?: number;
}

interface KPIsResponse {
  kpis: KPIs;
  readinessSummary: ReadinessSummary | null;
  readinessPerProperty: Record<string, ReadinessEntry> | null;
  readinessAsOf: string | null;
}

interface KpiHeadlineResponse {
  kpis: KPIs;
  dataFreshness: string | null;
}

export interface KpiBootstrapDailyRow {
  date: string;
  dayOfWeek: string;
  dayNum: number;
  occupancy: number;
  roomNights: number;
  adr: number;
  revpar: number;
  revenue: number;
  seriesType: 'actual' | 'otb';
}

export interface KpiBootstrapWeeklyBucket {
  index: number;
  label: string;
  start: string;
  end: string;
  occupancy: number;
  adr: number;
  revpar: number;
  revenue: number;
  roomNights: number;
}

export interface KpiBootstrapSourceStatus {
  source: string;
  available: boolean;
  rowCount: number;
  reason?: string | null;
  freshness?: string | null;
}

export interface KpiBootstrapIntelligence {
  dailyContext: {
    status: KpiBootstrapSourceStatus;
    data: Array<{
      stayDate: string;
      alos: number;
      pickup7d: number;
      cxl7d: number;
      minLos: number | null;
    }>;
  };
  stayPulse: {
    status: KpiBootstrapSourceStatus;
    data: Array<{
      stayDate: string;
      bookings: number;
      cancellations: number;
      refreshedAt: string | null;
    }>;
  };
  channels: {
    status: KpiBootstrapSourceStatus;
    data: Array<{
      channel: string;
      bookings: number;
      roomNights: number;
      revenue: number;
      adr: number;
      avgLeadTime: number;
      share: number;
    }>;
  };
  leadTime: {
    status: KpiBootstrapSourceStatus;
    data: Array<{
      bucket: string;
      roomNights: number;
      pct: number;
    }>;
  };
  compSet: {
    status: KpiBootstrapSourceStatus;
    data: Array<{
      checkDate: string;
      yourRate: number | null;
      compAvg: number | null;
      compFloor: number | null;
      positionPct: number | null;
      positionLabel: string;
      dayType: string | null;
    }>;
  };
}

export interface DailyReadAttentionProse {
  priority: number;
  title?: string;
  detail?: string;
  action?: string;
}

export interface DailyReadDriverProse {
  key: string;
  note?: string;
}

export interface DailyReadFinding {
  id: string;
  category: string;
  priority: number;
  score: number;
  urgency: 'today' | 'forward';
  confidence: 'high' | 'medium' | 'low';
  novelty: 'new' | 'continuing' | 'changed';
  title: string;
  detail: string;
  action: string;
  impact: string;
  tier: 'upside' | 'risk' | 'watch';
  scope: string;
}

export interface DailyReadTrendEvidence {
  id: string;
  dimension: 'demand_velocity' | 'rate_quality' | 'cancellation_pressure' | 'comparison_pace';
  role: 'directional' | 'context';
  direction: 'positive' | 'negative' | 'neutral';
  strength: number;
  title: string;
  detail: string;
  action: string;
  horizons: Array<3 | 7 | 28 | 30>;
}

export interface DailyReadDateException {
  stay_date: string;
  label: string;
  kind: 'soft' | 'strong';
  lead_days: number;
  reason: string;
  action: string;
}

export interface DailyReadTrendAssessment {
  direction: 'improving' | 'weakening' | 'mixed' | 'stable' | 'uncertain';
  confidence: 'high' | 'medium' | 'low';
  headline: string;
  dimension_headline?: string | null;
  pace_state?: string | null;
  pace_label?: string | null;
  positive_score: number;
  negative_score: number;
  evidence: DailyReadTrendEvidence[];
  date_exception?: DailyReadDateException | null;
}

export interface DailyReadObservabilityRow {
  propertyId: string;
  model: string;
  status?: string;
  rateMode?: string;
  totalRows: number;
  recentRows?: number;
  recentProperties?: number;
  latestAt?: string | null;
  ageHours?: number | null;
  auditRows?: number;
  propertySegmentUnknownRows: number;
  propertySegmentUnknownShare: number;
  tierUnknownRows: number;
  tierUnknownShare: number;
  schemaMismatchRows?: number;
  schemaMismatchShare?: number;
}

export interface DailyReadObservabilityGroup {
  scope: string;
  label: string;
  rows: DailyReadObservabilityRow[];
}

export interface DailyReadObservability {
  key: string;
  title: string;
  checkDate: string;
  status: 'ok' | 'degraded' | 'attention' | 'no_rows' | 'unavailable';
  summary?: string;
  alerts?: string[];
  groups?: DailyReadObservabilityGroup[];
}

export interface DailyReadProbCalibrationReliabilityRow {
  horizon: string;
  nValidation: number;
  valPositives: number;
  meanPredicted?: number | null;
  realizedConversion?: number | null;
  ratio?: number | null;
  absLogRatio?: number | null;
  trendDelta?: number | null;
  gate?: string;
  kept?: boolean;
  fittedAt?: string;
  version?: string;
}

export interface DailyReadProbCalibrationReliability {
  key: string;
  title: string;
  propertyId: string;
  checkDate: string;
  targetBand: { low: number; high: number };
  status: 'ok' | 'attention' | 'no_rows' | 'unavailable';
  summary?: string;
  alerts?: string[];
  rows?: DailyReadProbCalibrationReliabilityRow[];
}

/**
 * Nightly advisory for the Intelligence page, cached per property/day. Finding
 * selection and fallback copy are deterministic; when generation succeeds the
 * model synthesizes only the four top-level prose fields.
 */
export interface DailyReadNarrative {
  headline: string;
  summary: string;
  recommendationFor: string;
  recommendationPast: string;
  attention?: DailyReadAttentionProse[];
  /** Deterministic, ranked evidence. The model does not create or re-rank it. */
  findings?: DailyReadFinding[];
  /** Deterministic multi-window direction. The model explains but does not classify it. */
  trendAssessment?: DailyReadTrendAssessment;
  drivers?: DailyReadDriverProse[];
  analysisStatus?: 'generated' | 'fallback';
  analysisSource?: 'llm' | 'deterministic';
  analysisIssue?: string;
  observability?: DailyReadObservability;
  probCalibrationReliability?: DailyReadProbCalibrationReliability;
  observabilitySections?: DailyReadObservability[];
  /**
   * Whether a like-for-like prior-year baseline exists for this property. When
   * "no_baseline" (e.g. a property onboarded mid-2025), the page must render an
   * honest neutral "no baseline" message instead of a bare "no comp" — and
   * never anything implying healthy/on-pace.
   */
  comparison_status?: 'available' | 'thin_baseline' | 'no_baseline';
}

/**
 * Portfolio-wide pace vs the comparison year, one row per property. Surfaced as
 * a compact strip near the top of the Intelligence page regardless of the
 * currently-selected property. `delta_pct` is null / `status` is "no_baseline"
 * when a property has no like-for-like prior-year history. Guard every access —
 * the backend may send an empty array or omit the field entirely.
 */
export interface PortfolioPaceRow {
  property_id: string;
  property: string;
  current_revenue: number;
  comparison_revenue: number;
  delta_pct: number | null;
  status: 'behind' | 'ahead' | 'thin_baseline' | 'no_baseline';
  comparison_room_nights?: number;
  comparison_minimum_room_nights?: number;
}

export interface WhereToActSoftDate {
  stay_date: string;
  occ_pct: number;
  target_pct: number;
  dow: number | string;
  lead_days?: number;
}

export interface WhereToActEvent {
  event_name: string;
  start_date: string;
  end_date: string;
  city: string;
  venue_name: string | null;
  impact_tier: string | null;
  your_occ_pct: number | null;
}

export interface WhereToActModelDemand {
  stay_date: string;
  demand_label: string;
  demand_score: number;
}

/**
 * "Where to act" for the selected property — soft dates to defend, strong dates
 * + nearby events to push, and (clearly labelled as model output, not fact)
 * model demand forecast. Any field may be empty/absent — guard every access.
 */
export interface WhereToAct {
  soft_dates: WhereToActSoftDate[];
  strong_dates: WhereToActSoftDate[];
  nearby_events: WhereToActEvent[];
  model_demand: WhereToActModelDemand[];
}

export interface KpiBootstrapResponse {
  hero: {
    propertyId: string;
    propertyName: string;
    roomsTotal: number;
    occupancyPct: number;
    adr: number;
    revpar: number;
    otbRevenue: number;
    /** Room + ancillary fees (cleaning/parking), excl. tax. Room ADR/RevPAR unaffected. */
    totalRevenue: number;
    /** Total revenue per available room. */
    trevpar: number;
    pickup7dRevenue: number;
    pickup7dRoomNights: number;
  };
  pickup: PickupData;
  dailyBreakdown?: {
    period: { start: string; end: string; label: string };
    data: KpiBootstrapDailyRow[];
  } | null;
  forwardWeeklyBuckets: {
    start: string;
    end: string;
    weeks: KpiBootstrapWeeklyBucket[];
  };
  intelligence: KpiBootstrapIntelligence | null;
  dailyRead?: DailyReadNarrative | null;
  /** Portfolio-wide pace vs comparison year (one row per property). */
  portfolioPace: PortfolioPaceRow[];
  /** "Where to act" cues for the selected property. */
  whereToAct: WhereToAct | null;
  dataFreshness: string | null;
}

interface KpiReadinessResponse {
  readinessSummary: ReadinessSummary | null;
  readinessPerProperty: Record<string, ReadinessEntry> | null;
  readinessAsOf: string | null;
}

interface RawRoomType {
  id: string;
  name: string;
  base_rate?: number;
  unit_count?: number;
  display_order?: number | null;
  is_virtual?: boolean;
  virtual_consumes_key?: string;
  virtual_consumes_count?: number;
  pricing_source?: string | null;
  pricing_source_of_truth?: string | null;
  pricing_hierarchy_name?: string | null;
  fallback_hierarchy_offset?: number | null;
}

export interface DecisionsResponse {
  decisions: RawDecision[];
  data?: RawDecision[];
  counts: Counts;
}

interface LosGroupedResponse {
  groups: LosGroupedGroup[];
  error?: string;
}

export interface TrustReasonCount {
  reason: string;
  count: number;
}

export interface TrustMetrics {
  statusCounts: {
    pending: number;
    approved: number;
    skipped: number;
    blocked: number;
  };
  occupancySignalCounts: {
    roomType: number;
    propertyFallback: number;
    unknown: number;
  };
  roomTypeOccupancyUntrustedSkips: number;
  decisionStatusCounts: TrustReasonCount[];
  inputHealthCounts: TrustReasonCount[];
  topGuardrailCodes: TrustReasonCount[];
  topSkipReasons: TrustReasonCount[];
  topBlockReasons: TrustReasonCount[];
  visibilityExclusions: {
    total: number;
    summary: string | null;
  };
  currentRatesFreshness: {
    timestamp: string | null;
    ageMinutes: number | null;
    staleAfterMinutes: number | null;
    isStale: boolean;
  };
  generatedAt: string | null;
}

export interface SafetyRailsSettings {
  max_change_pct: number;
  floor_rate: number;
  ceiling_rate: number;
  require_healthy_data: boolean;
  exclude_event_days: boolean;
  min_confidence: number;
}

export interface TrustStateCheckSet {
  property_configured: boolean;
  room_hierarchy_configured: boolean;
  oracle_shadow_ready: boolean;
}

export interface TrustStateObserved {
  property_display_name?: string | null;
  room_hierarchy_count?: number;
  oracle_decisions_lookback?: number;
}

export interface TrustStateResponse {
  property_id: string;
  state: 'setup_required' | 'warming_up' | 'autonomy_eligible' | string;
  cold_start_active: boolean;
  block_autonomy: boolean;
  recommended_mode: 'manual' | 'hybrid' | 'autonomous' | string;
  reasons: string[];
  checks: TrustStateCheckSet;
  observed?: TrustStateObserved;
  generated_at?: string | null;
}

export interface AutonomySettingsResponse {
  property_id: string;
  autonomy_mode: 'manual' | 'hybrid' | 'autonomous';
  rate_mode?: 'manual' | 'hybrid' | 'autonomous';
  los_mode?: 'manual' | 'hybrid' | 'autonomous';
  notify_on_auto?: boolean;
  safety_rails?: SafetyRailsSettings;
  updated_at?: string | null;
  trust_state?: TrustStateResponse | null;
}

const DEFAULT_SAFETY_RAILS: SafetyRailsSettings = {
  max_change_pct: 10,
  floor_rate: 89,
  ceiling_rate: 499,
  require_healthy_data: true,
  exclude_event_days: true,
  min_confidence: 70,
};

const TRUST_METRICS_ENDPOINTS = [
  '/rates/trust-metrics',
  '/health/recommendation-trust',
  '/health/decision-trust',
  '/command-center/trust-metrics',
  '/decisions/trust-metrics',
] as const;

function toRecord(value: unknown): Record<string, unknown> | null {
  return value != null && typeof value === 'object' ? (value as Record<string, unknown>) : null;
}

function toCount(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Math.max(0, Math.round(value));
  }
  if (typeof value === 'string' && value.trim()) {
    const n = Number(value);
    return Number.isFinite(n) ? Math.max(0, Math.round(n)) : 0;
  }
  const record = toRecord(value);
  if (!record) return 0;
  return toCount(record.total ?? record.count ?? record.value ?? record.inWindow ?? 0);
}

function normalizeReasonCounts(value: unknown, limit: number = 3): TrustReasonCount[] {
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        const record = toRecord(item);
        if (!record) return null;
        const reasonRaw = record.reason ?? record.label ?? record.key ?? record.name;
        const reason =
          typeof reasonRaw === 'string' && reasonRaw.trim() ? reasonRaw.trim() : 'unknown';
        return {
          reason,
          count: toCount(record.count ?? record.total ?? record.value),
        };
      })
      .filter((item): item is TrustReasonCount => Boolean(item))
      .sort((a, b) => b.count - a.count)
      .slice(0, limit);
  }

  const record = toRecord(value);
  if (!record) return [];
  return Object.entries(record)
    .map(([reason, count]) => ({ reason, count: toCount(count) }))
    .filter((item) => item.count > 0)
    .sort((a, b) => b.count - a.count)
    .slice(0, limit);
}

function toMinutes(value: unknown): number | null {
  if (value == null) return null;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function normalizeTrustMetrics(payload: unknown): TrustMetrics {
  const envelope = toRecord(payload) ?? {};
  const root = toRecord(envelope.data) ?? envelope;
  const meta = toRecord(envelope.meta) ?? {};
  const counts = toRecord(root.status_counts) ?? toRecord(root.counts) ?? root;

  const explicitSkipReasons = normalizeReasonCounts(
    root.top_skip_reason_codes ?? root.topSkipReasonCodes,
  );
  const explicitBlockReasons = normalizeReasonCounts(
    root.top_block_reason_codes ?? root.topBlockReasonCodes,
  );
  const skipReasons = explicitSkipReasons.length
    ? explicitSkipReasons
    : normalizeReasonCounts(
        root.top_skip_reasons ?? root.skip_reasons ?? root.skipped_by_reason ?? root.skip_by_reason,
      );
  const blockReasons = explicitBlockReasons.length
    ? explicitBlockReasons
    : normalizeReasonCounts(
        root.top_block_reasons ??
          root.block_reasons ??
          root.blocked_reasons ??
          root.blocked_by_reason ??
          root.block_by_reason,
      );
  const decisionStatusCounts = normalizeReasonCounts(
    root.decision_status_counts ?? root.decisionStatusCounts,
    5,
  );
  const inputHealthCounts = normalizeReasonCounts(
    root.input_health_counts ?? root.inputHealthCounts,
    5,
  );
  const topGuardrailCodes = normalizeReasonCounts(
    root.top_guardrail_codes ?? root.topGuardrailCodes,
    5,
  );

  const visibility = toRecord(root.visibility_exclusions) ?? toRecord(root.visibility) ?? {};
  const visibilityTotal = toCount(
    visibility.total ??
      visibility.total_excluded ??
      visibility.excluded_count ??
      visibility.count ??
      visibility.total_dates,
  );
  const visibilityReasons = normalizeReasonCounts(
    visibility.reasons ?? visibility.by_reason ?? visibility.breakdown,
  );
  const visibilitySummaryRaw =
    visibility.summary ??
    visibility.message ??
    (visibilityReasons[0]
      ? `${visibilityReasons[0].reason}: ${visibilityReasons[0].count}`
      : visibilityTotal > 0
        ? `${visibilityTotal} exclusions`
        : null);

  const freshness = toRecord(root.current_rates_freshness) ?? {};
  const freshnessFromNested = toRecord(toRecord(root.freshness)?.current_rates);
  const currentRates = Object.keys(freshness).length > 0 ? freshness : (freshnessFromNested ?? {});
  const ageMinutes =
    toMinutes(
      currentRates.age_minutes ??
        currentRates.minutes_stale ??
        currentRates.age_min ??
        currentRates.age_mins,
    ) ??
    (() => {
      const ageSeconds = toMinutes(currentRates.age_seconds);
      return ageSeconds == null ? null : ageSeconds / 60;
    })();
  const staleAfterMinutes = toMinutes(
    currentRates.stale_after_minutes ??
      currentRates.threshold_minutes ??
      currentRates.warning_after_minutes,
  );
  const explicitStale = currentRates.is_stale;
  const isStale =
    typeof explicitStale === 'boolean'
      ? explicitStale
      : ageMinutes != null && staleAfterMinutes != null
        ? ageMinutes > staleAfterMinutes
        : false;

  return {
    statusCounts: {
      pending: toCount(counts.pending),
      approved: toCount(counts.approved),
      skipped: toCount(counts.skipped),
      blocked: toCount(counts.blocked),
    },
    occupancySignalCounts: {
      roomType: toCount(
        toRecord(root.occupancy_signal_counts)?.room_type ?? root.roomTypeOccupancyCount,
      ),
      propertyFallback: toCount(
        toRecord(root.occupancy_signal_counts)?.property_fallback ??
          root.propertyFallbackOccupancyCount,
      ),
      unknown: toCount(
        toRecord(root.occupancy_signal_counts)?.unknown ?? root.unknownOccupancySignalCount,
      ),
    },
    roomTypeOccupancyUntrustedSkips: toCount(
      root.room_type_occupancy_untrusted_skips ?? root.roomTypeOccupancyUntrustedSkips,
    ),
    decisionStatusCounts,
    inputHealthCounts,
    topGuardrailCodes,
    topSkipReasons: skipReasons,
    topBlockReasons: blockReasons,
    visibilityExclusions: {
      total: visibilityTotal,
      summary: typeof visibilitySummaryRaw === 'string' ? visibilitySummaryRaw : null,
    },
    currentRatesFreshness: {
      timestamp:
        (typeof currentRates.timestamp === 'string' && currentRates.timestamp) ||
        (typeof currentRates.as_of === 'string' && currentRates.as_of) ||
        (typeof currentRates.last_seen_at === 'string' && currentRates.last_seen_at) ||
        (typeof currentRates.pms_at === 'string' && currentRates.pms_at) ||
        null,
      ageMinutes,
      staleAfterMinutes,
      isStale,
    },
    generatedAt:
      (typeof root.generated_at === 'string' && root.generated_at) ||
      (typeof root.as_of === 'string' && root.as_of) ||
      (typeof meta.generated_at === 'string' && meta.generated_at) ||
      (typeof meta.as_of === 'string' && meta.as_of) ||
      null,
  };
}

function selectPropertyKpiRow(payload: Record<string, any>, property: string) {
  const propsArr = payload.data ?? payload.properties ?? [];
  return (
    propsArr.find((k: any) => k.property_id === property || k.property_name === property) ??
    propsArr[0]
  );
}

function normalizeKpisFromRow(propKpi: any): KPIs {
  const occRaw = propKpi?.occupancy ?? 0;
  const occPct = Math.round((occRaw || 0) * 100);
  const occLyRaw = propKpi?.mtd_occupancy_pct_ly;
  const occLyPct =
    typeof occLyRaw === 'number'
      ? Math.round((occLyRaw > 0 && occLyRaw <= 1 ? occLyRaw * 100 : occLyRaw) as number)
      : undefined;

  return {
    occupancy: occPct,
    occupancyLy: occLyPct,
    adr: Math.round(propKpi?.adr || 0),
    adrLy: typeof propKpi?.mtd_adr_ly === 'number' ? propKpi.mtd_adr_ly : undefined,
    revpar: Math.round(propKpi?.revpar || 0),
    revparLy: typeof propKpi?.mtd_revpar_ly === 'number' ? propKpi.mtd_revpar_ly : undefined,
    revenue: propKpi?.mtd_revenue || 0,
    pickup7dRevenue: propKpi?.pickup_7d_revenue || 0,
    pickup7dNights:
      typeof propKpi?.pickup_7d_nights === 'number'
        ? propKpi.pickup_7d_nights
        : typeof propKpi?.pickup_room_nights === 'number'
          ? propKpi.pickup_room_nights
          : undefined,
  };
}

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Fetch list of properties.
 *
 * Failures throw instead of returning a hardcoded list: callers fall back to
 * PropertyContext, which keeps the last good list. A hand-maintained default
 * here drifted and hid Conway, Grove, Magnolia and Quinley.
 */
export function usePropertiesQuery(opts?: { enabled?: boolean }) {
  const enabled = opts?.enabled ?? true;
  return useQuery({
    queryKey: queryKeys.properties,
    queryFn: async (): Promise<Property[]> => {
      const res = await authFetch(buildApiUrl('/properties', { include_watch_only: true }));
      if (!res.ok) {
        throw new Error(`Failed to load properties: ${res.status}`);
      }
      const data = await res.json();
      if (!Array.isArray(data?.properties)) {
        throw new Error('Failed to load properties: response had no property list');
      }
      if (data.config_source === 'fallback') {
        // The API instance hasn't loaded the live registry yet; its list is a short emergency roster.
        throw new Error('Failed to load properties: server is still loading its property registry');
      }
      return data.properties.map((p: any) => ({
        id: p.id,
        name: p.name,
        units: p.unit_count || 0,
        readOnly: Boolean(p.read_only),
      }));
    },
    enabled,
    staleTime: 60 * 60 * 1000, // 1 hour - properties rarely change
  });
}

export async function fetchRoomTypes(property: string): Promise<RoomType[]> {
  const res = await authFetch(buildApiUrl(`/room-types/${property}`));
  if (!res.ok) throw new Error(`Failed to load room types: ${res.status}`);
  const data = await res.json();
  if (data.error) {
    throw new Error(`Failed to load room types: ${data.error}`);
  }
  const types = data.room_types || [];
  return types.map((rt: RawRoomType) => {
    return {
      id: rt.id,
      name: rt.name,
      baseRate: rt.base_rate || 100,
      unitCount: rt.unit_count ?? null,
      displayOrder: rt.display_order ?? null,
      isVirtual: Boolean(rt.is_virtual),
      virtualConsumesKey: rt.virtual_consumes_key ?? null,
      virtualConsumesCount: rt.virtual_consumes_count ?? null,
      pricingSource: rt.pricing_source ?? null,
      pricingSourceOfTruth: rt.pricing_source_of_truth ?? null,
      pricingHierarchyName: rt.pricing_hierarchy_name ?? null,
      fallbackHierarchyOffset: rt.fallback_hierarchy_offset ?? null,
    };
  });
}

export const ROOM_TYPES_STALE_TIME_MS = 30 * 60 * 1000; // 30 min

/**
 * Fetch room types for a property
 */
export function useRoomTypesQuery(property: string, opts?: { enabled?: boolean }) {
  const enabled = opts?.enabled ?? true;
  return useQuery({
    queryKey: queryKeys.roomTypes(property),
    queryFn: () => fetchRoomTypes(property),
    enabled: !!property && enabled,
    staleTime: ROOM_TYPES_STALE_TIME_MS,
    placeholderData: keepPreviousData, // Show old data during property transition
  });
}

export interface DecisionsFetchOptions {
  lite?: boolean;
  includeCounts?: boolean;
  gridOnly?: boolean;
  countsOnly?: boolean;
  daysBack?: number;
  startDate?: string | null;
  endDate?: string | null;
  roomTypeIds?: string[];
  roomTypeScope?: 'all' | 'physical' | 'virtual';
  limit?: number;
}

export interface NormalizedDecisionsFetchOptions {
  lite: boolean;
  includeCounts: boolean;
  gridOnly: boolean;
  countsOnly: boolean;
  daysBack: number;
  startDate: string | null;
  endDate: string | null;
  roomTypeIds: string[];
  roomTypeScope: 'all' | 'physical' | 'virtual';
  limit: number | null;
  windowKey: string | null;
  surface: 'standard' | 'grid';
}

export function normalizeDecisionsFetchOptions(
  opts?: DecisionsFetchOptions,
): NormalizedDecisionsFetchOptions {
  const lite = opts?.lite ?? false;
  const includeCounts = opts?.includeCounts ?? true;
  const gridOnly = opts?.gridOnly ?? false;
  const countsOnly = opts?.countsOnly ?? false;
  const roomTypeScope = opts?.roomTypeScope ?? 'all';
  const limit =
    typeof opts?.limit === 'number' && Number.isFinite(opts.limit)
      ? Math.max(1, Math.min(50_000, Math.floor(opts.limit)))
      : null;
  const daysBack = Math.max(0, Math.round(opts?.daysBack ?? 0));
  const startDate = opts?.startDate ?? null;
  const endDate = opts?.endDate ?? null;
  const roomTypeIds = [...(opts?.roomTypeIds ?? [])]
    .map((id) => id.trim())
    .filter(Boolean)
    .sort();
  const windowKey =
    startDate || endDate || roomTypeIds.length > 0
      ? `${startDate ?? ''}:${endDate ?? ''}:${roomTypeIds.join(',')}`
      : null;
  const surface = gridOnly ? 'grid' : 'standard';
  return {
    lite,
    includeCounts,
    gridOnly,
    countsOnly,
    daysBack,
    startDate,
    endDate,
    roomTypeIds,
    roomTypeScope,
    limit,
    windowKey,
    surface,
  };
}

export function decisionsQueryKeyFor(
  property: string,
  daysAhead: number,
  normalized: NormalizedDecisionsFetchOptions,
) {
  const windowKey =
    normalized.limit === null
      ? normalized.windowKey
      : `${normalized.windowKey ?? ''}:limit=${normalized.limit}`;
  return queryKeys.decisions(
    property,
    daysAhead,
    normalized.lite ? 'lite' : 'full',
    normalized.surface,
    normalized.daysBack,
    normalized.countsOnly
      ? `counts:${windowKey ?? ''}`
      : normalized.roomTypeScope !== 'all'
        ? `${windowKey ?? ''}:scope=${normalized.roomTypeScope}`
        : windowKey,
  );
}

/**
 * Shared fetch + payload normalization for decisions. Used by both
 * useDecisionsQuery and the cross-property prefetcher so cached entries are
 * byte-identical regardless of which path populated them.
 */
export async function fetchDecisionsResponse(
  queryClient: ReturnType<typeof useQueryClient>,
  property: string,
  daysAhead: number,
  normalized: NormalizedDecisionsFetchOptions,
  signal?: AbortSignal,
): Promise<DecisionsResponse> {
  const {
    lite,
    includeCounts,
    gridOnly,
    countsOnly,
    daysBack,
    startDate,
    endDate,
    roomTypeIds,
    surface,
  } = normalized;
  const res = await authFetch(
    buildApiUrl('/rates/decisions', {
      property_name: property,
      days_ahead: daysAhead,
      ...(startDate ? { start_date: startDate } : {}),
      ...(endDate ? { end_date: endDate } : {}),
      ...(roomTypeIds.length > 0 ? { room_type_ids: roomTypeIds.join(',') } : {}),
      ...(normalized.roomTypeScope !== 'all' ? { room_type_scope: normalized.roomTypeScope } : {}),
      ...(normalized.limit !== null ? { limit: normalized.limit } : {}),
      ...(daysBack > 0 ? { days_back: daysBack } : {}),
      ...(lite ? { lite: '1' } : {}),
      ...(gridOnly ? { grid_only: '1', compact: '1' } : {}),
      ...(countsOnly ? { counts_only: '1' } : {}),
      ...(includeCounts ? {} : { include_counts: 'false' }),
    }),
    { signal },
  );
  if (!res.ok) throw new Error(`Failed to load decisions: ${res.status}`);
  const data = await res.json();
  const decisions = data.data ?? data.decisions ?? [];
  const responseCounts: Counts | null = data.counts
    ? {
        pending: {
          total: data.counts?.pending?.total || 0,
          inWindow: data.counts?.pending?.in_window || 0,
        },
        approved: {
          total: data.counts?.approved?.total || 0,
          inWindow: data.counts?.approved?.in_window || 0,
        },
        rejected: {
          total: data.counts?.rejected?.total || 0,
          inWindow: data.counts?.rejected?.in_window || 0,
        },
      }
    : null;
  const existingCounts =
    queryClient.getQueryData<DecisionsResponse>(
      decisionsQueryKeyFor(property, daysAhead, normalized),
    )?.counts ?? null;
  const fastCounts =
    queryClient.getQueryData<DecisionsResponse>(
      queryKeys.decisions(
        property,
        COMMAND_CENTER_FAST_DAYS_AHEAD,
        'lite',
        surface,
        daysBack,
        null,
      ),
    )?.counts ?? null;
  const reuseFastCounts =
    !includeCounts && daysAhead === COMMAND_CENTER_FULL_DAYS_AHEAD ? fastCounts : null;
  const counts =
    responseCounts ??
    (!includeCounts && gridOnly ? deriveCountsFromDecisions(decisions) : null) ??
    reuseFastCounts ??
    existingCounts ??
    EMPTY_COUNTS;
  return { decisions, counts };
}

/**
 * Fetch decisions for a property
 */
export function useDecisionsQuery(
  property: string,
  daysAhead: number = 730,
  opts?: DecisionsFetchOptions & { enabled?: boolean },
) {
  const queryClient = useQueryClient();
  const enabled = opts?.enabled ?? true;
  const normalized = normalizeDecisionsFetchOptions(opts);
  return useQuery({
    queryKey: decisionsQueryKeyFor(property, daysAhead, normalized),
    queryFn: ({ signal }) =>
      fetchDecisionsResponse(queryClient, property, daysAhead, normalized, signal),
    enabled: !!property && enabled,
    staleTime: 5 * 60 * 1000, // 5 min - decisions change more frequently
    // The app default is refetchOnWindowFocus: false (BigQuery-cost era). The
    // decision feed is PG-first and sub-second now, and PMS readback can land
    // minutes after a push settles — 'always' (not true, which staleTime would
    // suppress inside 5 min) so tabbing back to the grid shows live rates.
    refetchOnWindowFocus: 'always',
    // A prior property's decision payload must never appear under the newly
    // selected property while the next request is in flight. Besides confusing
    // current/proposed rates, old payloads can contain pre-containment comp data.
  });
}

/**
 * Fetch autonomy settings + safety rails for a property
 */
export function useAutonomySettingsQuery(property: string, opts?: { enabled?: boolean }) {
  const enabled = opts?.enabled ?? true;
  return useQuery({
    queryKey: queryKeys.autonomySettings(property),
    queryFn: async (): Promise<AutonomySettingsResponse> => {
      const res = await authFetch(buildApiUrl('/settings/autonomy', { property_id: property }));
      if (!res.ok) throw new Error(`Failed to load autonomy settings: ${res.status}`);
      const data = await res.json();
      return {
        property_id: data.property_id ?? property,
        autonomy_mode: data.autonomy_mode ?? 'manual',
        rate_mode: data.rate_mode ?? data.autonomy_mode ?? 'manual',
        los_mode: data.los_mode ?? 'manual',
        notify_on_auto: data.notify_on_auto ?? true,
        safety_rails: {
          max_change_pct: data.safety_rails?.max_change_pct ?? DEFAULT_SAFETY_RAILS.max_change_pct,
          floor_rate: data.safety_rails?.floor_rate ?? DEFAULT_SAFETY_RAILS.floor_rate,
          ceiling_rate: data.safety_rails?.ceiling_rate ?? DEFAULT_SAFETY_RAILS.ceiling_rate,
          require_healthy_data:
            data.safety_rails?.require_healthy_data ?? DEFAULT_SAFETY_RAILS.require_healthy_data,
          exclude_event_days:
            data.safety_rails?.exclude_event_days ?? DEFAULT_SAFETY_RAILS.exclude_event_days,
          min_confidence: data.safety_rails?.min_confidence ?? DEFAULT_SAFETY_RAILS.min_confidence,
        },
        updated_at: data.updated_at ?? null,
        trust_state: data.trust_state
          ? {
              property_id: data.trust_state.property_id ?? property,
              state: data.trust_state.state ?? 'setup_required',
              cold_start_active: Boolean(data.trust_state.cold_start_active),
              block_autonomy: Boolean(data.trust_state.block_autonomy),
              recommended_mode: data.trust_state.recommended_mode ?? 'manual',
              reasons: Array.isArray(data.trust_state.reasons)
                ? data.trust_state.reasons.filter(
                    (reason: unknown): reason is string => typeof reason === 'string',
                  )
                : [],
              checks: {
                property_configured: Boolean(data.trust_state.checks?.property_configured),
                room_hierarchy_configured: Boolean(
                  data.trust_state.checks?.room_hierarchy_configured,
                ),
                oracle_shadow_ready: Boolean(data.trust_state.checks?.oracle_shadow_ready),
              },
              observed: data.trust_state.observed ?? undefined,
              generated_at: data.trust_state.generated_at ?? null,
            }
          : null,
      };
    },
    enabled: !!property && property !== 'all' && enabled,
    staleTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
  });
}

/**
 * Fetch KPIs for a property
 */
export function useKPIsQuery(property: string, opts?: { enabled?: boolean }) {
  const enabled = opts?.enabled ?? true;
  return useQuery({
    queryKey: queryKeys.kpis(property),
    queryFn: async (): Promise<KPIsResponse> => {
      const res = await authFetch(buildApiUrl('/kpis', { property_name: property }));
      if (!res.ok) throw new Error(`Failed to load KPIs: ${res.status}`);
      const data = await res.json();
      const propKpi = selectPropertyKpiRow(data, property);
      const kpis = normalizeKpisFromRow(propKpi);
      return {
        kpis,
        readinessSummary: data.readiness_summary ?? null,
        readinessPerProperty: data.readiness_per_property ?? null,
        readinessAsOf: data.as_of ?? null,
      };
    },
    enabled: !!property && enabled,
    staleTime: 15 * 60 * 1000, // 15 min
    placeholderData: keepPreviousData, // Show old data during property transition
  });
}

export async function fetchKpiHeadline(property: string): Promise<KpiHeadlineResponse> {
  const res = await authFetch(buildApiUrl('/kpis/headline', { property_name: property }));
  if (!res.ok) throw new Error(`Failed to load KPI headline: ${res.status}`);
  const data = await res.json();
  const propKpi = selectPropertyKpiRow(data, property);
  return {
    kpis: normalizeKpisFromRow(propKpi),
    dataFreshness:
      data.data_freshness ?? data.meta?.data_freshness ?? propKpi?.refreshed_at ?? null,
  };
}

export const KPI_HEADLINE_STALE_TIME_MS = 5 * 60 * 1000;

/**
 * Fetch lightweight KPI headline values for Command Center chrome.
 */
export function useKpiHeadlineQuery(property: string, opts?: { enabled?: boolean }) {
  const enabled = opts?.enabled ?? true;
  return useQuery({
    queryKey: queryKeys.kpiHeadline(property),
    queryFn: () => fetchKpiHeadline(property),
    enabled: !!property && enabled,
    staleTime: KPI_HEADLINE_STALE_TIME_MS,
    placeholderData: keepPreviousData,
  });
}

export async function fetchKpiBootstrap(
  property: string,
  includeDailyBreakdown: boolean,
): Promise<KpiBootstrapResponse> {
  const res = await authFetch(
    buildApiUrl('/kpis/bootstrap', {
      property_name: property,
      include_daily_breakdown: includeDailyBreakdown ? 'true' : 'false',
    }),
  );
  if (!res.ok) throw new Error(`Failed to load KPI bootstrap: ${res.status}`);
  const data = await res.json();
  const hero = data.hero ?? {};
  const propertyMeta = data.property ?? {};
  const dailyBreakdown = data.daily_breakdown ?? null;
  const dailyRows = dailyBreakdown && Array.isArray(dailyBreakdown.data) ? dailyBreakdown.data : [];
  const forwardWeeklyBuckets = data.forward_weekly_buckets ?? {};
  const weekRows = Array.isArray(forwardWeeklyBuckets.weeks) ? forwardWeeklyBuckets.weeks : [];
  const intelligence = data.intelligence ?? null;
  const mapStatus = (status: any): KpiBootstrapSourceStatus => ({
    source: status?.source ?? '',
    available: Boolean(status?.available),
    rowCount: status?.row_count ?? 0,
    reason: status?.reason ?? null,
    freshness: status?.freshness ?? null,
  });

  // New optional contract fields — guard every access; the backend may send
  // an empty array, null, or omit them entirely.
  const portfolioPace: PortfolioPaceRow[] = Array.isArray(data.portfolio_pace)
    ? data.portfolio_pace.map((row: any) => {
        const statusRaw = String(row?.status ?? '').toLowerCase();
        const status: PortfolioPaceRow['status'] =
          statusRaw === 'ahead'
            ? 'ahead'
            : statusRaw === 'thin_baseline'
              ? 'thin_baseline'
              : statusRaw === 'no_baseline'
                ? 'no_baseline'
                : 'behind';
        return {
          property_id: String(row?.property_id ?? ''),
          property: String(row?.property ?? row?.property_id ?? ''),
          current_revenue: Number(row?.current_revenue ?? 0),
          comparison_revenue: Number(row?.comparison_revenue ?? 0),
          delta_pct:
            row?.delta_pct == null || !Number.isFinite(Number(row.delta_pct))
              ? null
              : Number(row.delta_pct),
          status,
          comparison_room_nights:
            row?.comparison_room_nights == null ? undefined : Number(row.comparison_room_nights),
          comparison_minimum_room_nights:
            row?.comparison_minimum_room_nights == null
              ? undefined
              : Number(row.comparison_minimum_room_nights),
        };
      })
    : [];

  const wta = data.where_to_act ?? null;
  const mapSoftDate = (row: any): WhereToActSoftDate => ({
    stay_date: String(row?.stay_date ?? ''),
    occ_pct: Number(row?.occ_pct ?? 0),
    target_pct: Number(row?.target_pct ?? 0),
    dow: row?.dow ?? '',
    lead_days: row?.lead_days == null ? undefined : Number(row.lead_days),
  });
  const whereToAct: WhereToAct | null = wta
    ? {
        soft_dates: Array.isArray(wta.soft_dates) ? wta.soft_dates.map(mapSoftDate) : [],
        strong_dates: Array.isArray(wta.strong_dates) ? wta.strong_dates.map(mapSoftDate) : [],
        nearby_events: Array.isArray(wta.nearby_events)
          ? wta.nearby_events.map((row: any) => ({
              event_name: String(row?.event_name ?? ''),
              start_date: String(row?.start_date ?? ''),
              end_date: String(row?.end_date ?? ''),
              city: String(row?.city ?? ''),
              venue_name: row?.venue_name ?? null,
              impact_tier: row?.impact_tier ?? null,
              your_occ_pct:
                row?.your_occ_pct == null || !Number.isFinite(Number(row.your_occ_pct))
                  ? null
                  : Number(row.your_occ_pct),
            }))
          : [],
        model_demand: Array.isArray(wta.model_demand)
          ? wta.model_demand.map((row: any) => ({
              stay_date: String(row?.stay_date ?? ''),
              demand_label: String(row?.demand_label ?? ''),
              demand_score: Number(row?.demand_score ?? 0),
            }))
          : [],
      }
    : null;

  return {
    hero: {
      propertyId: hero.property_id ?? propertyMeta.property_id ?? property,
      propertyName: hero.property_name ?? propertyMeta.property_name ?? property,
      roomsTotal: hero.rooms_total ?? propertyMeta.rooms_total ?? 0,
      occupancyPct:
        typeof hero.occupancy === 'number'
          ? Math.round(
              (hero.occupancy > 0 && hero.occupancy <= 1
                ? hero.occupancy * 100
                : hero.occupancy) as number,
            )
          : 0,
      adr: Math.round(hero.adr ?? 0),
      revpar: Math.round(hero.revpar ?? 0),
      otbRevenue: hero.mtd_revenue ?? 0,
      totalRevenue: hero.total_revenue ?? hero.mtd_revenue ?? 0,
      trevpar: Math.round(hero.trevpar ?? hero.revpar ?? 0),
      pickup7dRevenue: hero.pickup_7d_revenue ?? 0,
      pickup7dRoomNights: hero.pickup_7d_nights ?? 0,
    },
    pickup: data.pickup ?? {
      period: { type: '7d', start: '', end: '', label: 'Last 7 Days' },
      totals: { reservations: 0, room_nights: 0, revenue: 0, avg_lead_time: 0 },
      by_property: [],
      daily: [],
    },
    dailyBreakdown:
      dailyBreakdown == null
        ? null
        : {
            period: {
              start: dailyBreakdown.period?.start ?? '',
              end: dailyBreakdown.period?.end ?? '',
              label: dailyBreakdown.period?.label ?? '',
            },
            data: dailyRows.map((row: any) => ({
              date: row.date,
              dayOfWeek: row.day_of_week,
              dayNum: row.day_num,
              occupancy: row.occupancy ?? 0,
              roomNights: row.room_nights ?? 0,
              adr: row.adr ?? 0,
              revpar: row.revpar ?? 0,
              revenue: row.revenue ?? 0,
              seriesType: row.series_type === 'actual' ? 'actual' : 'otb',
            })),
          },
    forwardWeeklyBuckets: {
      start: forwardWeeklyBuckets.start ?? '',
      end: forwardWeeklyBuckets.end ?? '',
      weeks: weekRows.map((row: any) => ({
        index: row.index ?? 0,
        label: row.label ?? '',
        start: row.start ?? '',
        end: row.end ?? '',
        occupancy: row.occupancy ?? 0,
        adr: row.adr ?? 0,
        revpar: row.revpar ?? 0,
        revenue: row.revenue ?? 0,
        roomNights: row.room_nights ?? 0,
      })),
    },
    intelligence:
      intelligence == null
        ? null
        : {
            dailyContext: {
              status: mapStatus(intelligence.daily_context?.status),
              data: Array.isArray(intelligence.daily_context?.data)
                ? intelligence.daily_context.data.map((row: any) => ({
                    stayDate: row.stay_date ?? '',
                    alos: Number(row.alos ?? 0),
                    pickup7d: Number(row.pickup_7d ?? 0),
                    cxl7d: Number(row.cxl_7d ?? 0),
                    minLos: row.min_los == null ? null : Number(row.min_los),
                  }))
                : [],
            },
            stayPulse: {
              status: mapStatus(intelligence.stay_pulse?.status),
              data: Array.isArray(intelligence.stay_pulse?.data)
                ? intelligence.stay_pulse.data.map((row: any) => ({
                    stayDate: row.stay_date ?? '',
                    bookings: Number(row.bookings ?? 0),
                    cancellations: Number(row.cancellations ?? 0),
                    refreshedAt: row.refreshed_at ?? null,
                  }))
                : [],
            },
            channels: {
              status: mapStatus(intelligence.channels?.status),
              data: Array.isArray(intelligence.channels?.data)
                ? intelligence.channels.data.map((row: any) => ({
                    channel: row.channel ?? 'Unknown',
                    bookings: Number(row.bookings ?? 0),
                    roomNights: Number(row.room_nights ?? 0),
                    revenue: Number(row.revenue ?? 0),
                    adr: Number(row.adr ?? 0),
                    avgLeadTime: Number(row.avg_lead_time ?? 0),
                    share: Number(row.share ?? 0),
                  }))
                : [],
            },
            leadTime: {
              status: mapStatus(intelligence.lead_time?.status),
              data: Array.isArray(intelligence.lead_time?.data)
                ? intelligence.lead_time.data.map((row: any) => ({
                    bucket: row.bucket ?? '',
                    roomNights: Number(row.room_nights ?? 0),
                    pct: Number(row.pct ?? 0),
                  }))
                : [],
            },
            compSet: {
              status: mapStatus(intelligence.comp_set?.status),
              data: Array.isArray(intelligence.comp_set?.data)
                ? intelligence.comp_set.data.map((row: any) => ({
                    checkDate: row.check_date ?? '',
                    yourRate: row.your_rate == null ? null : Number(row.your_rate),
                    // Legacy bootstrap responses may still contain aggregate
                    // comp display scalars. The browser refuses to render them
                    // until the API exposes normalized quote authority.
                    compAvg: null,
                    compFloor: null,
                    positionPct: null,
                    positionLabel: 'NO_DATA',
                    dayType: row.day_type ?? null,
                  }))
                : [],
            },
          },
    dailyRead: (data.daily_read ?? null) as DailyReadNarrative | null,
    portfolioPace,
    whereToAct,
    dataFreshness: data.data_freshness ?? null,
  };
}

export const KPI_BOOTSTRAP_STALE_TIME_MS = 5 * 60 * 1000;

/**
 * Fetch the KPI page bootstrap payload for the default pickup/all-units first paint.
 */
export function useKpiBootstrapQuery(
  property: string,
  opts?: { enabled?: boolean; includeDailyBreakdown?: boolean },
) {
  const enabled = opts?.enabled ?? true;
  const includeDailyBreakdown = opts?.includeDailyBreakdown ?? true;
  return useQuery({
    queryKey: queryKeys.kpiBootstrap(property, includeDailyBreakdown),
    queryFn: () => fetchKpiBootstrap(property, includeDailyBreakdown),
    enabled: !!property && property !== 'all' && enabled,
    staleTime: KPI_BOOTSTRAP_STALE_TIME_MS,
    refetchOnWindowFocus: true,
    refetchInterval: KPI_BOOTSTRAP_STALE_TIME_MS,
  });
}

/**
 * Fetch readiness-only metadata for lightweight dashboard banners.
 */
export function useKpiReadinessQuery(property: string, opts?: { enabled?: boolean }) {
  const enabled = opts?.enabled ?? true;
  return useQuery({
    queryKey: queryKeys.kpiReadiness(property),
    queryFn: async (): Promise<KpiReadinessResponse> => {
      const res = await authFetch(buildApiUrl('/kpis/readiness', { property_name: property }));
      if (!res.ok) throw new Error(`Failed to load KPI readiness: ${res.status}`);
      const data = await res.json();
      return {
        readinessSummary: data.readiness_summary ?? null,
        readinessPerProperty: data.readiness_per_property ?? null,
        readinessAsOf: data.as_of ?? null,
      };
    },
    enabled: !!property && enabled,
    staleTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
  });
}

/**
 * Fetch freshness data for a property (DEFERRED - waits for grid)
 */
export function useFreshnessQuery(property: string, enabled: boolean = true) {
  return useQuery({
    queryKey: queryKeys.freshness(property),
    queryFn: async (): Promise<Freshness | null> => {
      // NO try/catch - let errors propagate so isError works
      const res = await authFetch(buildApiUrl('/oracle/freshness', { property_name: property }));
      if (!res.ok) throw new Error(`Freshness fetch failed: ${res.status}`);
      const data = await res.json();
      return data.freshness ?? null;
    },
    enabled: !!property && enabled,
    staleTime: 5 * 60 * 1000, // 5 min
    retry: 0, // No retries - fail fast, surface via deferredDegraded
    placeholderData: keepPreviousData, // Show old data during property transition
  });
}

/**
 * Fetch recommendation trust metrics for Command Center explainability panel.
 */
export function useTrustMetricsQuery(
  property: string,
  enabled: boolean = true,
  opts?: { lite?: boolean },
) {
  const lite = opts?.lite ?? false;
  return useQuery({
    queryKey: queryKeys.trustMetrics(property, lite ? 'lite' : 'full'),
    queryFn: async (): Promise<TrustMetrics> => {
      let lastError: Error | null = null;

      for (const endpoint of TRUST_METRICS_ENDPOINTS) {
        const url = buildApiUrl(endpoint, {
          property_id: property,
          property_name: property,
          ...(lite ? { lite: '1' } : {}),
        });
        const res = await authFetch(url);
        if (res.ok) {
          const payload = await res.json();
          return normalizeTrustMetrics(payload);
        }

        if (res.status !== 404 && res.status !== 405) {
          throw new Error(`Trust metrics fetch failed: ${res.status}`);
        }
        lastError = new Error(`Trust metrics endpoint not found: ${endpoint}`);
      }

      throw lastError ?? new Error('Trust metrics endpoint unavailable');
    },
    enabled: !!property && enabled,
    staleTime: 60 * 1000,
    retry: 0,
    placeholderData: keepPreviousData,
  });
}

/**
 * Fetch events for a property (DEFERRED - waits for grid)
 */
export function useEventsQuery(property: string, days: number = 730, enabled: boolean = true) {
  return useQuery({
    queryKey: queryKeys.events(property, days),
    queryFn: async (): Promise<EventsMap> => {
      // NO try/catch - let errors propagate so isError works
      const res = await authFetch(
        buildApiUrl('/events/stacking', { property_name: property, days }),
      );
      if (!res.ok) throw new Error(`Events fetch failed: ${res.status}`);
      const data = await res.json();

      // Parse events into map
      const eventsMap: EventsMap = {};
      if (data.stacked_days) {
        for (const day of data.stacked_days) {
          if (day.events && day.events.length > 0) {
            eventsMap[day.date] = day.events.map((e: any) => {
              const score =
                typeof e.compression_score === 'number' && Number.isFinite(e.compression_score)
                  ? e.compression_score
                  : 0;
              const compressionLabel =
                typeof e.compression_label === 'string' ? e.compression_label : null;
              return {
                name: e.title,
                category: e.venue || 'Event',
                score,
                compressionLabel,
                impactTier: resolveEventImpactTier(compressionLabel, score),
              };
            });
          }
        }
      }
      return eventsMap;
    },
    enabled: !!property && enabled,
    staleTime: 30 * 60 * 1000, // 30 min - events don't change often
    retry: 0, // No retries - fail fast, surface via deferredDegraded
    placeholderData: keepPreviousData, // Show old data during property transition
  });
}

export function useIntelEventsQuery(property: string, days: number = 730, enabled: boolean = true) {
  return useQuery({
    queryKey: queryKeys.intelEvents(property, days),
    queryFn: async (): Promise<EventsMap> => {
      // NO try/catch - let errors propagate so isError works
      const res = await authFetch(
        buildApiUrl('/events/opportunities', { property_name: property, days, lite: '1' }),
      );
      if (!res.ok) throw new Error(`Events fetch failed: ${res.status}`);
      const data = await res.json();

      const cfg = getIntelEventsConfig(property);

      // Parse events into map
      const eventsMap: EventsMap = {};
      const opportunities: Array<{
        date?: string;
        events?: BackendOpportunityEvent[];
        compression_score?: number;
      }> = Array.isArray(data?.opportunities) ? data.opportunities : [];

      for (const opp of opportunities) {
        const date = String(opp?.date || '').slice(0, 10);
        if (!date) continue;

        const rawEvents: BackendOpportunityEvent[] = Array.isArray(opp?.events) ? opp.events : [];
        if (rawEvents.length === 0) continue;

        const filtered = rawEvents.filter((ev) => {
          const rel = typeof ev.relevance_score === 'number' ? ev.relevance_score : null;
          return rel == null ? true : rel >= cfg.minRelevance;
        });

        const deduped = dedupeNearIdenticalEvents(date, filtered);
        const ranked = rankEventsByImpact(deduped).slice(0, cfg.maxPerDay);

        if (ranked.length === 0) continue;

        eventsMap[date] = ranked.map((ev) => {
          const score =
            typeof ev.compression_score === 'number' && Number.isFinite(ev.compression_score)
              ? ev.compression_score
              : computeEventCompressionScore(opp?.compression_score, ev.relevance_score);
          const compressionLabel =
            typeof ev.compression_label === 'string' ? ev.compression_label : null;
          return {
            name: String(ev.title || '').trim(),
            category: String(ev.venue || ev.category || 'Event'),
            score,
            compressionLabel,
            impactTier: resolveEventImpactTier(compressionLabel, score),
          };
        });
      }
      return eventsMap;
    },
    enabled: !!property && enabled,
    staleTime: 30 * 60 * 1000, // 30 min - events don't change often
    retry: 0, // No retries - fail fast, surface via deferredDegraded
    placeholderData: keepPreviousData, // Show old data during property transition
  });
}

/**
 * Fetch LOS restrictions for a property (DEFERRED - waits for grid)
 */
export function useLOSRestrictionsQuery(
  property: string,
  enabled: boolean = true,
  daysAhead: number = COMMAND_CENTER_FAST_DAYS_AHEAD,
) {
  return useQuery({
    queryKey: queryKeys.losRestrictions(property, daysAhead),
    queryFn: async (): Promise<LOSRestriction[]> => fetchLOSRestrictions(property, daysAhead),
    enabled: !!property && enabled,
    staleTime: 10 * 60 * 1000, // 10 min - restrictions change less frequently
    placeholderData: keepPreviousData,
  });
}

/**
 * Fetch pending LOS recommendations for a property (same priority feed as LOS Adjustments list).
 */
export function usePendingLosRecommendationsQuery(property: string, enabled: boolean = true) {
  return useQuery({
    queryKey: queryKeys.pendingLosRecommendations(property),
    queryFn: async (): Promise<LosItem[]> => {
      const groupedRes = await authFetch(
        buildApiUrl(`/priorities/grouped/${encodeURIComponent(property)}`, {
          limit: 120,
          action_type: 'LOS_CHANGE',
        }),
      );
      if (!groupedRes.ok) {
        throw new Error(`Failed to load LOS priorities: ${groupedRes.status}`);
      }

      const groupedData = (await groupedRes.json()) as LosGroupedResponse;
      if (groupedData.error) {
        throw new Error(`LOS priorities unavailable: ${groupedData.error}`);
      }

      const rawGroups = groupedData.groups || [];
      const hasActionTypes = rawGroups.some(
        (group) =>
          Array.isArray(group.action_types) ||
          (typeof group.action_types === 'string' && group.action_types.trim().length > 0),
      );
      const losGroups = hasActionTypes
        ? rawGroups.filter((group) => isLosActionType(group.action_types))
        : rawGroups;

      const groupIds = Array.from(
        new Set(losGroups.map((group) => group.group_id).filter(Boolean)),
      );
      if (groupIds.length === 0) return [];

      const batchRes = await authFetch(buildApiUrl('/priorities/groups'), {
        method: 'POST',
        body: JSON.stringify({
          group_ids: groupIds,
          property_id: property,
        }),
      });
      if (!batchRes.ok) {
        throw new Error(`Failed to load LOS group details: ${batchRes.status}`);
      }

      const batchData = (await batchRes.json()) as LosGroupBatchResponse;
      if (batchData.error) {
        throw new Error(`LOS group details unavailable: ${batchData.error}`);
      }

      return buildLosItems(losGroups, batchData).filter(
        (item) =>
          item.proposed_los !== item.current_los ||
          losVectorHasNonMinChange(item.restriction_json ?? null),
      );
    },
    enabled: !!property && enabled,
    staleTime: 2 * 60_000,
    retry: 0,
    placeholderData: keepPreviousData,
  });
}

// ============================================================================
// Convenience hook to invalidate all Command Center data
// ============================================================================

export function useInvalidateCommandCenterData() {
  const queryClient = useQueryClient();

  return (property?: string) => {
    if (property) {
      queryClient.invalidateQueries({ queryKey: queryKeys.roomTypes(property) });
      queryClient.invalidateQueries({
        queryKey: queryKeys.decisions(property, COMMAND_CENTER_FAST_DAYS_AHEAD, 'lite'),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.decisions(property, COMMAND_CENTER_FAST_DAYS_AHEAD, 'lite', 'grid'),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.decisions(property, COMMAND_CENTER_FULL_DAYS_AHEAD),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.decisions(property, COMMAND_CENTER_FULL_DAYS_AHEAD, 'lite'),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.decisions(property, COMMAND_CENTER_FULL_DAYS_AHEAD, 'lite', 'grid'),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.kpis(property) });
      queryClient.invalidateQueries({ queryKey: queryKeys.kpiHeadline(property) });
      queryClient.invalidateQueries({ queryKey: queryKeys.kpiReadiness(property) });
      queryClient.invalidateQueries({ queryKey: queryKeys.freshness(property) });
      queryClient.invalidateQueries({ queryKey: queryKeys.trustMetrics(property) });
      queryClient.invalidateQueries({
        queryKey: queryKeys.events(property, COMMAND_CENTER_FAST_DAYS_AHEAD),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.events(property, COMMAND_CENTER_FULL_DAYS_AHEAD),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.intelEvents(property, COMMAND_CENTER_FAST_DAYS_AHEAD),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.intelEvents(property, COMMAND_CENTER_FULL_DAYS_AHEAD),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.losRestrictions(property) });
      queryClient.invalidateQueries({ queryKey: queryKeys.pendingLosRecommendations(property) });
    } else {
      queryClient.invalidateQueries({ queryKey: ['roomTypes'] });
      queryClient.invalidateQueries({ queryKey: ['decisions'] });
      queryClient.invalidateQueries({ queryKey: ['kpis'] });
      queryClient.invalidateQueries({ queryKey: ['kpiHeadline'] });
      queryClient.invalidateQueries({ queryKey: ['kpiReadiness'] });
      queryClient.invalidateQueries({ queryKey: ['freshness'] });
      queryClient.invalidateQueries({ queryKey: ['trustMetrics'] });
      queryClient.invalidateQueries({ queryKey: ['events'] });
      queryClient.invalidateQueries({ queryKey: ['losRestrictions'] });
      queryClient.invalidateQueries({ queryKey: ['pendingLosRecommendations'] });
    }
  };
}
